'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  position: string;
  company: string;
  content: string;
  rating: number;
  project: string;
  outcome: string;
}

const ClientTestimonials = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Rajesh Kumar",
      position: "Network Operations Manager",
      company: "Leading Telecom Operator",
      content: "Ativanam Solutions has been our trusted partner for tower maintenance across 200+ sites. Their 2-hour emergency response time and proactive maintenance approach has helped us achieve 99.9% network uptime. The technical expertise of their team is exceptional.",
      rating: 5,
      project: "Telecom Tower AMC",
      outcome: "99.9% uptime achieved across 200+ sites"
    },
    {
      id: 2,
      name: "Priya Sharma",
      position: "Facility Manager",
      company: "Manufacturing Corporation",
      content: "The electrical system upgrade completed by Ativanam was flawless. They completed the 2 MVA transformer installation ahead of schedule with zero downtime to our operations. Their attention to safety protocols and quality standards is commendable.",
      rating: 5,
      project: "HT/LT Panel Installation",
      outcome: "Zero downtime during critical upgrade"
    },
    {
      id: 3,
      name: "Amit Patel",
      position: "Project Director",
      company: "Smart City Development Authority",
      content: "Working with Ativanam on our smart city infrastructure project was a great experience. They integrated 75 sites seamlessly, meeting all government compliance requirements. Their project management and technical delivery exceeded our expectations.",
      rating: 5,
      project: "Smart City Integration",
      outcome: "75 sites integrated on schedule"
    },
    {
      id: 4,
      name: "Suresh Reddy",
      position: "Operations Head",
      company: "Industrial Park",
      content: "Ativanam's comprehensive AMC service has transformed our maintenance operations. Their preventive maintenance approach has reduced our emergency callouts by 80% and significantly improved our power infrastructure reliability.",
      rating: 5,
      project: "Annual Maintenance Contract",
      outcome: "80% reduction in emergency callouts"
    }
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
              Client Success Stories
            </h2>
          </div>
        </div>
      </section>
    );
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-16 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-accent/10 rounded-full border border-accent/30 mb-4">
            <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-accent mr-2" />
            <span className="text-sm font-body font-semibold text-accent">Client Testimonials</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
            Client Success Stories
          </h2>
          <p className="text-lg text-textSecondary font-body max-w-3xl mx-auto">
            Hear from our clients about their experience working with Ativanam Solutions and the measurable results we delivered
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-5xl mx-auto">
          <div className="bg-card rounded-lg border border-border p-8 lg:p-12 shadow-subtle">
            {/* Quote Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="ChatBubbleBottomCenterTextIcon" size={32} className="text-primary" />
              </div>
            </div>

            {/* Rating */}
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(currentTestimonial.rating)].map((_, i) => (
                <Icon key={i} name="StarIcon" size={24} className="text-accent fill-current" />
              ))}
            </div>

            {/* Testimonial Content */}
            <blockquote className="text-center mb-8">
              <p className="text-lg lg:text-xl text-textPrimary font-body leading-relaxed mb-6">
                "{currentTestimonial.content}"
              </p>
            </blockquote>

            {/* Client Info */}
            <div className="text-center mb-6">
              <h4 className="text-xl font-headline font-bold text-primary mb-1">
                {currentTestimonial.name}
              </h4>
              <p className="text-sm text-textSecondary font-body mb-1">
                {currentTestimonial.position}
              </p>
              <p className="text-sm text-textSecondary font-body font-semibold">
                {currentTestimonial.company}
              </p>
            </div>

            {/* Project Details */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-6 border-t border-border">
              <div className="flex items-center gap-2">
                <Icon name="BriefcaseIcon" size={20} className="text-primary" />
                <span className="text-sm font-body text-textSecondary">
                  <span className="font-semibold text-textPrimary">Project:</span> {currentTestimonial.project}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="ChartBarIcon" size={20} className="text-success" />
                <span className="text-sm font-body text-textSecondary">
                  <span className="font-semibold text-textPrimary">Outcome:</span> {currentTestimonial.outcome}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 w-12 h-12 flex items-center justify-center bg-card rounded-full border border-border shadow-subtle transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-110"
            aria-label="Previous testimonial"
          >
            <Icon name="ChevronLeftIcon" size={24} />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 w-12 h-12 flex items-center justify-center bg-card rounded-full border border-border shadow-subtle transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-110"
            aria-label="Next testimonial"
          >
            <Icon name="ChevronRightIcon" size={24} />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex items-center justify-center gap-3 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? 'w-12 h-3 bg-primary' :'w-3 h-3 bg-border hover:bg-primary/50'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* Client Retention Stat */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-4 px-8 py-4 bg-success/10 rounded-lg border border-success/30">
            <Icon name="UserGroupIcon" size={32} className="text-success" />
            <div className="text-left">
              <div className="text-3xl font-headline font-bold text-success">98%</div>
              <div className="text-sm font-body text-textSecondary">Client Retention Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientTestimonials;